"use client";

import { useEffect, useCallback, useRef } from "react";
import { useAuth } from "../providers/AuthProvider";
import { apiFetch } from "../lib/api";
import { useStore } from "../store";
import { useAgentList, useAgentsLoading } from "../store/selectors";
import type { Agent, AgentTokenInfo, NewTokenResponse } from "../store/types";

export type { Agent, AgentTokenInfo, NewTokenResponse } from "../store/types";

export function useAgents() {
  const { authenticated, token } = useAuth();
  const agents = useAgentList();
  const loading = useAgentsLoading();
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const fetchAgents = useCallback(async () => {
    const t = tokenRef.current;
    if (!t) {
      useStore.getState().setAgents([]);
      return;
    }

    useStore.getState().setAgentsLoading(true);
    try {
      const data = await apiFetch<Agent[]>("/agents", t);
      useStore.getState().setAgents(data);
    } catch {
      useStore.getState().setAgentsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authenticated) {
      fetchAgents();
    } else {
      useStore.getState().setAgents([]);
    }
  }, [authenticated, fetchAgents]);

  const registerAgent = useCallback(
    async (name: string, description?: string): Promise<Agent> => {
      const t = tokenRef.current;
      if (!t) throw new Error("Not authenticated");
      const agent = await apiFetch<Agent>("/agents", t, {
        method: "POST",
        body: JSON.stringify({ name, description }),
      });
      await fetchAgents();
      return agent;
    },
    [fetchAgents]
  );

  const updateAgent = useCallback(
    async (
      agentId: string,
      updates: { name?: string; description?: string; status?: string }
    ): Promise<Agent> => {
      const t = tokenRef.current;
      if (!t) throw new Error("Not authenticated");
      const agent = await apiFetch<Agent>(`/agents/${agentId}`, t, {
        method: "PATCH",
        body: JSON.stringify(updates),
      });
      await fetchAgents();
      return agent;
    },
    [fetchAgents]
  );

  const getToken = useCallback(
    async (agentId: string): Promise<AgentTokenInfo | null> => {
      const t = tokenRef.current;
      if (!t) throw new Error("Not authenticated");
      try {
        return await apiFetch<AgentTokenInfo>(`/agents/${agentId}/token`, t);
      } catch {
        return null;
      }
    },
    []
  );

  const createToken = useCallback(
    async (agentId: string, label?: string): Promise<NewTokenResponse> => {
      const t = tokenRef.current;
      if (!t) throw new Error("Not authenticated");
      return apiFetch<NewTokenResponse>(`/agents/${agentId}/token`, t, {
        method: "POST",
        body: JSON.stringify({ label }),
      });
    },
    []
  );

  const revokeToken = useCallback(
    async (agentId: string): Promise<void> => {
      const t = tokenRef.current;
      if (!t) throw new Error("Not authenticated");
      await apiFetch(`/agents/${agentId}/token`, t, {
        method: "DELETE",
      });
    },
    []
  );

  return {
    agents,
    loading,
    error: null,
    refetch: fetchAgents,
    registerAgent,
    updateAgent,
    getToken,
    createToken,
    revokeToken,
  };
}
